import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import chatService from '../../../services/chatService';
import { useI18n } from '../../../i18n/I18nContext';

export default function EditMessageModal({ message, onClose, onSaved }) {
  const { t } = useI18n();
  const [body, setBody] = useState(message.body || '');
  const [errors, setErrors] = useState([]);
  const [sending, setSending] = useState(false);
  const panel = useRef(null);
  const mounted = useRef(false);
  const inFlight = useRef(false);
  const callbacks = useRef({ onClose, onSaved });
  callbacks.current = { onClose, onSaved };

  useEffect(() => {
    mounted.current = true;
    const previousFocus = document.activeElement;
    panel.current?.querySelector('textarea')?.focus();
    const keydown = event => {
      if (event.key === 'Escape' && !inFlight.current) callbacks.current.onClose();
      if (event.key === 'Tab') {
        const controls = Array.from(panel.current?.querySelectorAll('textarea:not(:disabled), button:not(:disabled)') || []);
        const first = controls[0], last = controls[controls.length - 1];
        if ((event.shiftKey && document.activeElement === first) || (!event.shiftKey && document.activeElement === last)) {
          event.preventDefault();
          (event.shiftKey ? last : first)?.focus();
        }
      }
    };
    document.addEventListener('keydown', keydown);
    return () => {
      mounted.current = false;
      document.removeEventListener('keydown', keydown);
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, []);

  const submit = async event => {
    event.preventDefault();
    if (inFlight.current) return;
    inFlight.current = true;
    setSending(true);
    setErrors([]);
    const failure = t('talk.editing.failed', {}, 'Could not edit this message. Please try again.');
    try {
      const result = await chatService.editMessage(message.id, body);
      if (!mounted.current) return;
      if (!result.status || !result.data?.message) {
        setErrors([result.message || failure]);
        return;
      }
      callbacks.current.onSaved(result.data.message);
      callbacks.current.onClose();
    } catch (error) {
      if (mounted.current) setErrors(error.status === 422
        ? Object.values(error.errors || {}).flat()
        : [failure]);
    } finally {
      inFlight.current = false;
      if (mounted.current) setSending(false);
    }
  };

  return createPortal(
    <div className="talk-forward-layer" onClick={event => {
      if (event.target === event.currentTarget && !inFlight.current) onClose();
    }}>
      <form className="talk-forward-panel talk-edit-panel" ref={panel} role="dialog" aria-modal="true"
        aria-labelledby="talk-edit-title" aria-busy={sending} onSubmit={submit}>
        <h2 id="talk-edit-title">{t('talk.editing.title', {}, 'Edit message')}</h2>
        <label htmlFor="talk-edit-body">{t('talk.editing.body', {}, 'Message text')}</label>
        <textarea id="talk-edit-body" value={body} onChange={event => setBody(event.target.value)}
          disabled={sending} aria-invalid={errors.length > 0} aria-describedby={errors.length ? 'talk-edit-errors' : undefined} />
        {errors.length > 0 && <div id="talk-edit-errors" role="alert" className="talk-action-error">
          {errors.map((error, index) => <p key={index}>{error}</p>)}
        </div>}
        <div className="talk-edit-actions">
          <button type="submit" className="talk-edit-save" disabled={sending}>{t('common.actions.save', {}, 'Save')}</button>
          <button type="button" className="talk-edit-cancel" disabled={sending} onClick={onClose}>{t('common.actions.cancel', {}, 'Cancel')}</button>
        </div>
      </form>
    </div>, document.body
  );
}
